/**
 * What an import batch's state and a receipt's verdict mean (issue #96).
 *
 * The pills that render these live in `components/records/ImportState.tsx`. The
 * split is not decorative: a module exporting both a component and constants
 * breaks Fast Refresh, and the page imports the counts helpers below without
 * rendering a pill.
 *
 * Six verdicts, six distinct treatments. Collapsing them into "green and grey"
 * is the exact failure `TermState.tsx` was written to correct, and here it
 * would erase the two distinctions the record depends on: `rejected` (refused,
 * with a stated reason) is not `skipped` (passed over), and `would_create` (a
 * dry run's projection) is not `created` (a row that now exists).
 */
import type { ImportBatch, ImportKind, ImportMode, ImportStatus, ReceiptAction } from "@/lib/api/imports";

/** No `default`: academic state must not wear the tenant accent (badge-status.test.ts). */
type BadgeVariant = "secondary" | "outline" | "destructive" | "success" | "warning" | "info";

export const RECEIPT_ACTION: Record<ReceiptAction, { label: string; variant: BadgeVariant }> = {
  created: { label: "Created", variant: "success" },
  would_create: { label: "Would create", variant: "info" },
  matched: { label: "Matched", variant: "secondary" },
  skipped: { label: "Skipped", variant: "outline" },
  corrected: { label: "Corrected", variant: "warning" },
  rejected: { label: "Rejected", variant: "destructive" },
};

export const BATCH_STATUS: Record<ImportStatus, { label: string; variant: BadgeVariant }> = {
  running: { label: "Running", variant: "info" },
  completed: { label: "Completed", variant: "success" },
  failed: { label: "Failed", variant: "destructive" },
};

/**
 * The hints describe what the MODE does, in the present tense, rather than what
 * a given run did: a failed apply wrote nothing, so "wrote to the record" would
 * be a claim about an outcome the mode alone cannot make. The run's own status
 * carries that.
 */
export const IMPORT_MODE: Record<ImportMode, { label: string; variant: BadgeVariant; hint: string }> = {
  dry_run: { label: "Dry run", variant: "outline", hint: "Projects an apply - writes no academic record" },
  apply: { label: "Apply", variant: "secondary", hint: "Writes to the academic record" },
};

/** What kind of source a run read, for a person. An older batch carries no kind and is historical. */
export const IMPORT_KIND: Record<ImportKind, { label: string; hint: string }> = {
  historical: { label: "Historical", hint: "A closed term source; each learner without a source ID needs your choice" },
  registrar_roster: { label: "Registrar roster", hint: "A live-term roster; each learner without a source ID needs your choice" },
};

export function importKind(batch: Pick<ImportBatch, "kind">): ImportKind {
  return batch.kind ?? "historical";
}

/** An applied registrar roster recorded students the administrator may go on to invite (ADR 0036). */
export function invitesStudents(batch: Pick<ImportBatch, "kind" | "mode">): boolean {
  return batch.mode === "apply" && importKind(batch) === "registrar_roster";
}

export const ENTITY_KIND_LABEL: Record<string, string> = {
  term: "Term",
  course: "Course",
  faculty: "Faculty",
  student: "Student",
  enrollment: "Enrollment",
  outcome: "Outcome",
};

/**
 * What an observation is called, for a person.
 *
 * The API's `kind` is a machine name, and rendering it directly put
 * `SOURCE_CRN_REUSED` on screen as a heading. `observationLabel` falls back to a
 * de-slugged version rather than a placeholder, so a kind added on the server
 * before this map catches up still reads as words instead of vanishing.
 */
export const OBSERVATION_LABEL: Record<string, string> = {
  source_crn_reused: "A source CRN appears in more than one term",
  legacy_sid_in_standard_space: "Legacy student IDs land in the standard sequence",
};

export function observationLabel(kind: string): string {
  const known = OBSERVATION_LABEL[kind];
  if (known) return known;
  const words = kind.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : kind;
}

/**
 * A verdict that names an IMAM row, and is therefore correctable.
 *
 * `skipped`, `rejected` and `would_create` resolve to nothing — migration 034's
 * `ck_lms_import_receipt_resolution` makes that structural — so there is no row
 * for a correction to supersede.
 */
export function namesARow(action: ReceiptAction): boolean {
  return action === "created" || action === "matched" || action === "corrected";
}

/**
 * The counts a batch is entitled to report.
 *
 * A dry run reports what an apply WOULD create; it never reports creations,
 * because it made none. Reading `created_count` for a dry run would always show
 * zero, which is true but reads as "this file adds nothing".
 */
export function batchCounts(batch: ImportBatch): { label: string; value: number }[] {
  return [
    batch.mode === "dry_run"
      ? { label: "Would create", value: batch.projected_create_count }
      : { label: "Created", value: batch.created_count },
    { label: "Matched", value: batch.matched_count },
    { label: "Skipped", value: batch.skipped_count },
    { label: "Rejected", value: batch.rejected_count },
  ];
}

/**
 * Whether a run licenses an apply of the material now in hand.
 *
 * The DIGEST has to match, not merely exist. A dry run describes the bytes it
 * read, so the moment the operator edits the payload the projection on screen is
 * about a file that is no longer the one an apply would write, and an apply
 * cleared by it would be authorised by a report of something else. Comparing
 * digests is what makes "dry run first" a property of the material rather than
 * of the order the buttons happened to be pressed.
 *
 * An apply does not clear another apply. Its receipts say what was written, not
 * what a further run would do, and re-reading them as a projection is the same
 * category error as reading `created_count` for a dry run.
 *
 * A run that did not complete clears nothing either: a batch closed `failed`
 * carries the counts it reached rather than the counts of the whole file, so it
 * is a partial account and not a projection of one.
 *
 * The SOURCE NAME has to match as well, because it is recorded and it is what
 * a person reads. It is not covered by the digest: retyping the label leaves
 * the bytes untouched, so a dry run named "registrar-2019.json" would otherwise
 * clear an apply that files the identical rows in `lms_import_batch` under
 * "registrar-2020.json", and the history list names each run by that label. A
 * dry run cleared one described run, label included, not merely one payload.
 */
export function clearsApply(
  run: ImportBatch | null | undefined,
  digest: string | null,
  sourceName: string,
  kind: ImportKind = "historical",
): boolean {
  return (
    !!run &&
    !!digest &&
    run.mode === "dry_run" &&
    run.status === "completed" &&
    run.source_digest === digest &&
    run.source_name === sourceName &&
    importKind(run) === kind
  );
}

/** The first twelve digest characters, which is what an operator compares by eye. */
export function shortDigest(digest: string): string {
  return digest.slice(0, 12);
}

/** The accessible name of the table button that opens a run.
 *
 * The source name alone will not do. Re-running one source is the ordinary
 * case for this feature — that is what idempotency is FOR — so the list
 * routinely holds several rows carrying the identical source string, and a
 * button named only after the source leaves a screen-reader or keyboard user
 * hearing the same label three times with no way to tell the dry run from the
 * apply. The sighted reading comes from the mode and time COLUMNS, which a
 * button name does not reach; this puts those two facts into the name itself.
 *
 * The narrow-viewport card needs no such label: it is one button wrapping the
 * mode, the counts and the time, so its name already comes from that content.
 * An `aria-label` there would REPLACE the content rather than supplement it,
 * which is why only the table cell gets one.
 */
export function runButtonLabel(batch: ImportBatch, started: string): string {
  return `${batch.source_name} - ${IMPORT_MODE[batch.mode].label}, ${started}`;
}
